import { useState } from "react";
import { useTranslation } from "react-i18next";

import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import { Box, IconButton, Link, Tooltip, Typography } from "@mui/material";

import { dayjs } from "@thalamos/common";
import type {
  RafUserMetadata,
  ReasonableAdjustment,
  ReasonableAdjustmentClinicalEntry,
  ReasonableAdjustmentsFlagResponse,
} from "@thalamos/aspire-common";

import {
  Button,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { useNhsFlags } from "~/hooks/useNhsFlags.js";
import { useOdsOrganisations } from "~/hooks/useOdsOrganisation.js";

/** Map of ODS code -> resolved organisation name (missing codes fall back). */
type OrgNames = Record<string, string>;

/**
 * Popout dialog rendering the NHS Reasonable Adjustments Flag record.
 *
 * Role split (server-decided):
 *  - `clinical` (B0257/B0380): full record — adjustments, conditions, and
 *    record-level "created on" / "last updated on" lines.
 *  - `non-clinical` (B0264): adjustments-only list. No conditions, no
 *    record-level metadata.
 */
export function RafPopout({
  open,
  onClose,
  flags,
}: {
  open: boolean;
  onClose: () => void;
  flags: ReasonableAdjustmentsFlagResponse;
}) {
  const { t } = useTranslation();

  // Resolve every "…at <org>" ODS code in the record in one batch, de-duped, so
  // the (usually identical) org code is fetched once rather than per line.
  const orgNames = useOdsOrganisations(collectOrgCodes(flags));

  return (
    <PopupDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <PopupDialogTitle
        titleText={t("layout.patientBanner.raf.dialogTitle")}
        closeDialog={onClose}
      />

      <Box sx={{ p: 3 }}>
        <RafBody flags={flags} orgNames={orgNames} />

        <GuidanceLinks />

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            label={t("layout.patientBanner.raf.close")}
            onClick={onClose}
          />
        </Box>
      </Box>
    </PopupDialog>
  );
}

/**
 * ODS codes are short, space-free alphanumerics (e.g. "U31269"). The Provenance
 * `onBehalfOf.display` field sometimes carries a free-text organisation name
 * instead (e.g. "York Infirmary"), which ODS can't resolve — so only attempt
 * resolution for values that actually look like a code.
 */
function isLikelyOdsCode(value: string): boolean {
  return /^[A-Za-z0-9]{3,12}$/.test(value);
}

/** Every ODS-code-shaped organisation value referenced anywhere in the record. */
function collectOrgCodes(flags: ReasonableAdjustmentsFlagResponse): string[] {
  const metas: (RafUserMetadata | null)[] =
    flags.role === "non-clinical"
      ? flags.adjustments.map((a) => a.addedBy)
      : flags.flags
        ? [
            ...flags.flags.adjustments.map((a) => a.addedBy),
            ...flags.flags.impairments.map((e) => e.addedBy),
            ...flags.flags.conditions.map((e) => e.addedBy),
            flags.flags.createdBy,
            flags.flags.lastUpdatedBy,
          ]
        : [];

  return metas
    .map((m) => m?.organisationName)
    .filter((code): code is string => code != null && isLikelyOdsCode(code));
}

function RafBody({
  flags,
  orgNames,
}: {
  flags: ReasonableAdjustmentsFlagResponse;
  orgNames: OrgNames;
}) {
  const { t } = useTranslation();

  if (flags.role === "non-clinical") {
    return (
      <AdjustmentsSection adjustments={flags.adjustments} orgNames={orgNames} />
    );
  }

  // Clinical
  if (!flags.flags) {
    return <Typography>{t("layout.patientBanner.raf.noRecordedNeed")}</Typography>;
  }

  const { adjustments, impairments, conditions, createdBy, lastUpdatedBy } =
    flags.flags;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <AdjustmentsSection adjustments={adjustments} orgNames={orgNames} />

      <ClinicalEntrySection
        title={t("layout.patientBanner.raf.impairments")}
        entries={impairments}
        orgNames={orgNames}
      />
      <ClinicalEntrySection
        title={t("layout.patientBanner.raf.underlyingConditions")}
        entries={conditions}
        orgNames={orgNames}
      />

      {(createdBy || lastUpdatedBy) && (
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
          {createdBy && (
            <RafMetadataLine
              label={t("layout.patientBanner.raf.createdOn")}
              meta={createdBy}
              orgNames={orgNames}
              variant="body2"
            />
          )}
          {lastUpdatedBy && (
            <RafMetadataLine
              label={t("layout.patientBanner.raf.lastUpdatedOn")}
              meta={lastUpdatedBy}
              orgNames={orgNames}
              variant="body2"
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function AdjustmentsSection({
  adjustments,
  orgNames,
}: {
  adjustments: ReasonableAdjustment[];
  orgNames: OrgNames;
}) {
  const { t } = useTranslation();

  if (adjustments.length === 0) {
    return <Typography>{t("layout.patientBanner.raf.noRecordedNeed")}</Typography>;
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {t("layout.patientBanner.raf.recordedAdjustments")}
      </Typography>
      <Box
        component="ul"
        sx={{ display: "flex", flexDirection: "column", gap: 2, pl: 3 }}
      >
        {adjustments.map((a, i) => (
          <li key={`${a.code}-${i}`}>
            <Typography>{a.display}</Typography>
            {a.supportingComments && (
              <SupportingComments text={a.supportingComments} />
            )}
            <RafMetadataLine
              label={t("layout.patientBanner.raf.added")}
              meta={a.addedBy}
              orgNames={orgNames}
              variant="caption"
            />
          </li>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Renders a clinical-only list (impairments or underlying conditions) with the
 * same per-item shape as adjustments — description + supporting comments +
 * "Added: …" metadata line. `title` is passed in already-translated.
 */
function ClinicalEntrySection({
  title,
  entries,
  orgNames,
}: {
  title: string;
  entries: ReasonableAdjustmentClinicalEntry[];
  orgNames: OrgNames;
}) {
  const { t } = useTranslation();

  if (entries.length === 0) return null;
  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Box
        component="ul"
        sx={{ display: "flex", flexDirection: "column", gap: 2, pl: 3 }}
      >
        {entries.map((e, i) => (
          <li key={`${e.code}-${i}`}>
            <Typography>{e.display}</Typography>
            {e.supportingComments && (
              <SupportingComments text={e.supportingComments} />
            )}
            <RafMetadataLine
              label={t("layout.patientBanner.raf.added")}
              meta={e.addedBy}
              orgNames={orgNames}
              variant="caption"
            />
          </li>
        ))}
      </Box>
    </Box>
  );
}

/**
 * External guidance links the local IT system SHOULD provide alongside the
 * Reasonable Adjustments record (NHS spec).
 */
function GuidanceLinks() {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
      <Typography variant="subtitle2" gutterBottom>
        {t("layout.patientBanner.raf.furtherGuidance")}
      </Typography>
      <Box
        component="ul"
        sx={{ pl: 3, m: 0, display: "flex", flexDirection: "column", gap: 0.5 }}
      >
        <li>
          <Link
            href="https://www.gov.uk/guidance/equality-act-2010-guidance"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("layout.patientBanner.raf.guidance.equalityAct")}
          </Link>
        </li>
        <li>
          <Link
            href="https://www.england.nhs.uk/accessible-information-standard/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("layout.patientBanner.raf.guidance.accessibleInformationStandard")}
          </Link>
        </li>
      </Box>
    </Box>
  );
}

/**
 * Free-text supporting comments. RAF notes can run to thousands of characters,
 * so long ones are clamped to a few lines with a Show more / Show less toggle.
 */
function SupportingComments({ text }: { text: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 300;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography
        variant="body2"
        sx={{
          fontStyle: "italic",
          whiteSpace: "pre-line",
          ...(isLong &&
            (expanded
              ? // Expanded: cap the height and scroll within, so the very long
                // (often boilerplate) note can't push "Show less" off-screen.
                { maxHeight: "12rem", overflowY: "auto" }
              : // Collapsed: a 3-line teaser keeps the list scannable.
                {
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                })),
        }}
      >
        {text}
      </Typography>
      {isLong && (
        <Link
          component="button"
          type="button"
          variant="body2"
          sx={{ mt: 0.5 }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded
            ? t("layout.patientBanner.raf.showLess")
            : t("layout.patientBanner.raf.showMore")}
        </Link>
      )}
    </Box>
  );
}

/**
 * One metadata line, in the NHS-mandated format:
 *   `<label> <date> by <user's name> at <organisation name>`
 *
 * `organisationName` from the FHIR Provenance is sometimes an ODS code and
 * sometimes a free-text name. Code-shaped values are resolved to a human-
 * readable name via the `orgNames` map (looked up once, in batch, by the
 * dialog); anything else — a free-text name, or a code ODS didn't recognise —
 * renders as-is. `userName` is an SDS role identifier, not an ODS org, so it is
 * never resolved. Missing pieces fall back to an em-dash.
 */
function RafMetadataLine({
  label,
  meta,
  orgNames,
  variant,
}: {
  label: string;
  meta: RafUserMetadata;
  orgNames: OrgNames;
  variant: "body2" | "caption";
}) {
  const { t } = useTranslation();

  // NHS spec mandates dd-Mmm-yyyy (e.g. 27-May-2026) for any date in a RAF record.
  const date = meta.date ? dayjs(meta.date).format("DD-MMM-YYYY") : "—";
  const user = meta.userName ?? "—";
  const code = meta.organisationName;
  const org = (code && orgNames[code]) || code || "—";

  return (
    <Typography variant={variant} color="text.secondary">
      {label}{" "}
      {t("layout.patientBanner.raf.metadataLine", { date, user, org })}
    </Typography>
  );
}

/**
 * True if the RAF record has at least one recorded adjustment — used to
 * decide whether the banner icon is highlighted (cue) or muted.
 */
export function rafHasContent(flags: ReasonableAdjustmentsFlagResponse): boolean {
  if (flags.role === "non-clinical") {
    return flags.adjustments.length > 0;
  }
  return !!flags.flags && flags.flags.adjustments.length > 0;
}

/**
 * Reasonable Adjustments indicator shown in the patient banner. Self-contained:
 * reads its own data and owns the popout state.
 *
 * Renders nothing only when RAF doesn't apply (org doesn't have rafEnabled, no
 * NHS number, or the record is restricted/blocked). When RAF applies it always
 * shows — clicking opens the section, which displays either the recorded
 * adjustments or, per RAR002, the "no current recorded need" message. Icon is
 * highlighted when adjustments exist (the visual cue) and muted when there
 * are none.
 */
export function RafIndicator({
  patientId,
  nhsNumber,
}: {
  patientId: string;
  nhsNumber: string | null | undefined;
}) {
  const { t } = useTranslation();
  const { flags } = useNhsFlags({ patientId, nhsNumber });
  const [open, setOpen] = useState(false);

  if (!flags) {
    return null;
  }

  const hasContent = rafHasContent(flags);

  return (
    <>
      <Tooltip
        title={
          hasContent
            ? t("layout.patientBanner.raf.indicator.tooltipRecorded")
            : t("layout.patientBanner.raf.indicator.tooltipNone")
        }
      >
        <IconButton
          aria-label={t("layout.patientBanner.raf.indicator.ariaLabel")}
          onClick={(e) => {
            // Banner wraps this area in a Box with its own onClick that opens
            // the edit-patient flyout — stop the click bubbling so only our
            // popout opens.
            e.stopPropagation();
            setOpen(true);
          }}
          size="small"
        >
          <AccessibilityNewIcon
            fontSize="small"
            color={hasContent ? "primary" : "disabled"}
          />
        </IconButton>
      </Tooltip>
      {/*
       * The popout renders in a portal, but React events still bubble through
       * the React tree to the banner row's onClick (which opens "Edit patient").
       * Stop popout clicks (e.g. "Show more") here so they don't trigger it.
       */}
      <Box onClick={(e) => e.stopPropagation()}>
        <RafPopout open={open} onClose={() => setOpen(false)} flags={flags} />
      </Box>
    </>
  );
}
